import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { Mail, MessageCircle, Monitor } from 'react-feather';
import { toast } from 'react-toastify';
import { getNotificationLogsAPI, muteNotificationAPI, retryNotificationAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import DataTableWrapper from '../../../Components/shared/DataTableWrapper';
import DrawerPanel from '../../../Components/shared/DrawerPanel';
import { companyUrl } from '../../../utils/mhNav';

function ChannelIcon({ ch }) {
  if (ch === 'Teams') return <Monitor size={16} />;
  if (ch === 'WhatsApp') return <MessageCircle size={16} />;
  return <Mail size={16} />;
}

export default function NotificationLog() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [payloadOpen, setPayloadOpen] = useState(false);
  const [filters, setFilters] = useState({
    meetingQuery: '',
    channel: 'all',
    status: 'all',
    dateFrom: '',
    dateTo: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getNotificationLogsAPI(filters);
      setRows(res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      { name: 'Time', selector: (r) => new Date(r.sentAt).toLocaleString(), width: '160px' },
      { name: 'Meeting', selector: (r) => r.meetingTitle, grow: 1 },
      { name: 'Recipient', selector: (r) => r.recipient, width: '160px' },
      {
        name: 'Channel',
        width: '80px',
        cell: (r) => <ChannelIcon ch={r.channel} />,
      },
      { name: 'Type', selector: (r) => r.type, width: '120px' },
      {
        name: 'Status',
        width: '120px',
        cell: (r) => {
          if (r.status === 'retrying') {
            return (
              <span className="text-warning d-inline-flex align-items-center gap-1">
                <Spinner size="sm" animation="border" />
                Retrying
              </span>
            );
          }
          const variant = r.status === 'delivered' ? 'success' : r.status === 'failed' ? 'danger' : 'secondary';
          return <span className={`badge bg-${variant}`}>{r.status}</span>;
        },
      },
      {
        name: 'Actions',
        width: '200px',
        cell: (r) => (
          <div className="d-flex flex-wrap gap-1">
            <Button size="sm" variant="outline-secondary" onClick={() => retryNotificationAPI(r.id).then(() => { toast.success('Retry'); load(); })}>
              Retry
            </Button>
            <Button size="sm" variant="outline-primary" onClick={() => setDrawer(r)}>
              Open Payload
            </Button>
            <Button size="sm" variant="light" onClick={() => muteNotificationAPI(r.id).then(() => toast.success('Muted'))}>
              Mute
            </Button>
          </div>
        ),
      },
    ],
    [load]
  );

  if (error && !rows.length) {
    return (
      <div>
        <PageHeader title="Notification log" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Notifications', path: companyUrl('notifications') }, { label: 'Log' }]} />
        <p className="text-danger">Failed to load.</p>
        <Button onClick={load}>Retry</Button>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Notification log"
        breadcrumbs={[
          { label: 'Company', path: companyUrl('dashboard') },
          { label: 'Notifications', path: companyUrl('notifications') },
          { label: 'Log' },
        ]}
      />
      {loading && !rows.length ? (
        <LoadingSkeleton rows={5} cols={4} />
      ) : (
        <>
          <Row className="g-2 mb-3">
            <Col md={3}>
              <Form.Control size="sm" placeholder="Meeting search" value={filters.meetingQuery} onChange={(e) => setFilters((f) => ({ ...f, meetingQuery: e.target.value }))} />
            </Col>
            <Col md={2}>
              <Form.Select size="sm" value={filters.channel} onChange={(e) => setFilters((f) => ({ ...f, channel: e.target.value }))}>
                <option value="all">All channels</option>
                <option value="Email">Email</option>
                <option value="Teams">Teams</option>
                <option value="WhatsApp">WhatsApp</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select size="sm" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
                <option value="all">All status</option>
                <option value="delivered">Delivered</option>
                <option value="failed">Failed</option>
                <option value="pending">Pending</option>
                <option value="retrying">Retrying</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Control type="date" size="sm" value={filters.dateFrom} onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))} />
            </Col>
            <Col md={2}>
              <Form.Control type="date" size="sm" value={filters.dateTo} onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))} />
            </Col>
          </Row>
          <DataTableWrapper columns={columns} data={rows} loading={loading} showSearch={false} />
        </>
      )}

      <DrawerPanel isOpen={Boolean(drawer)} onClose={() => setDrawer(null)} title="Delivery detail" width={560}>
        {drawer && (
          <>
            <div className="small text-muted">Provider response (truncated)</div>
            <pre className="small bg-light p-2 rounded">{drawer.providerResponse?.slice(0, 120)}…</pre>
            <Button size="sm" variant="link" onClick={() => setPayloadOpen(true)}>Expand</Button>
            <div className="fw-semibold mt-3">Attempts</div>
            <ul className="small">
              {(drawer.attempts || []).map((a, i) => (
                <li key={i}>{new Date(a.at).toLocaleString()} — {a.result}: {a.detail}</li>
              ))}
            </ul>
            <div className="d-flex gap-2">
              <Button size="sm" onClick={() => retryNotificationAPI(drawer.id).then(() => toast.success('Retry'))}>Retry</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => toast.success('Download (mock)')}>Download Payload</Button>
            </div>
          </>
        )}
      </DrawerPanel>

      <Modal show={payloadOpen} onHide={() => setPayloadOpen(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>Full payload</Modal.Title></Modal.Header>
        <Modal.Body>
          <pre className="small">{drawer?.payloadPreview}</pre>
          <pre className="small mt-2">{drawer?.providerResponse}</pre>
        </Modal.Body>
      </Modal>
    </div>
  );
}
